
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAudioRecorder } from './useAudioRecorder';

interface UseChatInputProps {
    messageText: string;
    setMessageText: (text: string) => void;
    onSend: () => void | Promise<void>;
    onSendMedia?: (file: File, caption?: string) => void;
    onPasteFiles?: (files: File[]) => void;
}

export const useChatInput = ({
    messageText,
    setMessageText,
    onSend,
    onSendMedia,
    onPasteFiles
}: UseChatInputProps) => {
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showQuickReplies, setShowQuickReplies] = useState(false);

    // Audio Recorder Hook Integration
    const {
        isRecording,
        isPaused,
        isPreviewPlaying,
        canResumePreview,
        previewProgress,
        recordingTime,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        togglePreviewPlayback,
        seekPreview,
        cancelRecording,
        formatTime
    } = useAudioRecorder((file) => onSendMedia?.(file));

    const onSendRef = useRef(onSend);
    onSendRef.current = onSend;
    const quickReplySearchTerm = useMemo(() => {
        if (messageText.startsWith('/')) {
            return messageText.slice(1);
        }
        return null;
    }, [messageText]);

    useEffect(() => {
        setShowQuickReplies(messageText.startsWith('/'));
    }, [messageText]);

    const handleQuickReplySelect = useCallback((content: string) => {
        setMessageText(content);
        setShowQuickReplies(false);
    }, [setMessageText]);

    const handleQuickReplyClose = useCallback(() => {
        setShowQuickReplies(false);
    }, []);

    const toggleEmojiPicker = useCallback(() => {
        setShowEmojiPicker(prev => !prev);
    }, []);

    const handleEmojiSelect = useCallback((emoji: string) => {
        setMessageText(messageText + emoji);
        setShowEmojiPicker(false);
    }, [messageText, setMessageText]);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        if (!onPasteFiles) return;

        const clipboardData = e.clipboardData;
        if (!clipboardData) return;

        const files: File[] = [];
        const items = clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) files.push(file);
            }
        }

        if (files.length > 0) {
            e.preventDefault();
            onPasteFiles(files);
        }
    }, [onPasteFiles]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (showQuickReplies && ['Enter', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void onSendRef.current();
        }
    }, [showQuickReplies]);

    const showSendButton = messageText.trim().length > 0 || isRecording;

    return {
        emojiButtonRef,
        showEmojiPicker,
        setShowEmojiPicker,
        showQuickReplies,
        quickReplySearchTerm,
        showSendButton,
        isRecording,
        isPaused,
        isPreviewPlaying,
        canResumePreview,
        previewProgress,
        recordingTime,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        togglePreviewPlayback,
        seekPreview,
        cancelRecording,
        formatTime,
        toggleEmojiPicker,
        handleEmojiSelect,
        handleQuickReplySelect,
        handleQuickReplyClose,
        handleKeyDown,
        handlePaste
    };
};
